import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, Circle, XCircle, Clock, RotateCw, StopCircle, X } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useSocket } from '../../context/SocketContext';  // ✅ Shared Socket.IO connection (single connection per tab)

import { logger, getErrorMessage } from '../../utils/logger';
import { API_BASE_URL } from '../../config/api';  // ✅ Industry Standard: Environment-aware URLs
// ============================================================================
// TYPES
// ============================================================================

interface PipelineStage {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  details?: string[];
  estimatedTime?: string;
  errorMessage?: string;
}

interface PipelineConfig {
  operationId: string;
  operationType: 'recording' | 'ai-generation' | 'bulk-execution' | 'cloning' | string;
  title: string;
  completionMessage: string;
  stages: PipelineStage[];
  flowId?: string;        // NEW: For navigation to specific test
  folderId?: string | null; // NEW: For folder expansion
  flowName?: string;      // NEW: For personalized messages
  onComplete?: () => void;
  onClose?: () => void;
}

// ============================================================================
// GLOBAL PIPELINE MODAL COMPONENT
// Thread-safe, operation-isolated, multi-user capable
// ============================================================================

export const GlobalPipelineModal: React.FC = () => {
  // ✅ Thread-safe: Each operation stored by unique ID
  const [activePipelines, setActivePipelines] = useState<Map<string, PipelineConfig>>(new Map());
  const { socket } = useSocket();  // ✅ Shared Socket.IO connection from SocketContext
  const [isBackgrounded, setIsBackgrounded] = useState(false);
  const [backgroundToastId, setBackgroundToastId] = useState<string | null>(null);
  const [retryingStage, setRetryingStage] = useState<string | null>(null);
  const [isAborting, setIsAborting] = useState(false);
  const navigate = useNavigate();
  const { showToast, updateToast } = useToast();

  // Show the most recent operation for this user
  const currentPipeline = useMemo(() => {
    if (activePipelines.size === 0) return null;
    // Get latest operation (FIFO - most recent last)
    const pipelines = Array.from(activePipelines.values());
    return pipelines[pipelines.length - 1] || null;
  }, [activePipelines]);

  const allComplete = currentPipeline?.stages.every(s => s.status === 'complete') ?? false;
  const hasError = currentPipeline?.stages.some(s => s.status === 'error') ?? false;

  // ============================================================================
  // WEBSOCKET SETUP
  // ============================================================================

  useEffect(() => {
    if (!socket) return;

    // ============================================================================
    // INDUSTRY STANDARD: Data-Driven UI (React, Vue, Angular)
    // ============================================================================
    // Pattern: Render from backend-provided configuration
    // Frontend receives pipeline structure, backend defines it
    // ============================================================================
    
    // ✅ EVENT: Recording stopped, prepare for processing
    socket.on('recording-stopped', (data: { 
      recordingId: string; 
      flowId: string;
      folderId: string | null;
      flowName: string;
      pipeline: {
        id: string;
        type: string;
        platform: string;
        stages: Array<{
          id: string;
          name: string;
          description: string;
          required: boolean;
          condition?: string;
          insertBefore?: string;
        }>;
      };
    }) => {
      logger.debug('Recording stopped', {
        component: 'PIPELINE-MODAL',
        recordingId: data.recordingId,
        flowId: data.flowId,
        folderId: data.folderId || 'Root',
        platform: data.pipeline.platform,
        stagesCount: data.pipeline.stages.length
      });
      
      data.pipeline.stages.forEach((stage, idx) => {
        const conditionInfo = stage.condition ? ` [Condition: ${stage.condition}]` : '';
        logger.info(`   ${idx + 1}. ${stage.name} (${stage.id})${conditionInfo}`);
      });
      console.groupEnd();
      
      // Join operation-specific room
      socket.emit('join-pipeline-room', { operationId: data.recordingId });
      
      // Convert backend stage definitions to frontend format
      const initialStages = data.pipeline.stages.map(stage => ({
        ...stage,
        status: 'pending' as const
      }));
      
      // Initialize modal with backend-provided stages
      setActivePipelines(prev => {
        const updated = new Map(prev);
        updated.set(data.recordingId, {
          operationId: data.recordingId,
          operationType: data.pipeline.type,
          title: 'Processing Your Recording...',
          completionMessage: `${data.flowName} is ready!`,
          flowId: data.flowId,
          folderId: data.folderId,
          flowName: data.flowName,
          stages: initialStages  // ✅ Backend-provided stages only
        });
        return updated;
      });
    });

    // ✅ EVENT: Pipeline explicitly started (alternative trigger)
    socket.on('pipeline-started', (data: { operationId: string; config: PipelineConfig }) => {
      logger.debug(`[PIPELINE-MODAL] Pipeline started: ${data.operationId}`);
      
      // Join operation-specific room
      socket.emit('join-pipeline-room', { operationId: data.operationId });
      
      // Add to active pipelines
      setActivePipelines(prev => {
        const updated = new Map(prev);
        updated.set(data.operationId, data.config);
        return updated;
      });
    });

    // ✅ EVENT: Stage update (processing, complete, error)
    socket.on('pipeline-stage-update', (data: { 
      operationId: string; 
      stageId: string; 
      status: 'pending' | 'processing' | 'complete' | 'error';
      details?: string[];
      errorMessage?: string;
    }) => {
      logger.debug(`[PIPELINE-MODAL] ${data.operationId} → Stage ${data.stageId}: ${data.status}`);
      
      setActivePipelines(prev => {
        const updated = new Map(prev);
        const pipeline = updated.get(data.operationId);
        if (!pipeline) {
          logger.warn(`[PIPELINE-MODAL] Pipeline not found: ${data.operationId}`);
          return prev;
        }

        pipeline.stages = pipeline.stages.map(stage =>
          stage.id === data.stageId
            ? { 
                ...stage, 
                status: data.status, 
                details: data.details, 
                errorMessage: data.errorMessage 
              }
            : stage
        );
        
        // Update toast if backgrounded (simplified - no counter)
        if (isBackgrounded && backgroundToastId) {
          const currentStage = pipeline.stages.find(s => s.id === data.stageId);
          
          updateToast(backgroundToastId, {
            message: `${pipeline.flowName} - ${currentStage?.name || data.stageId}`
          });
          
          logger.debug(`[PIPELINE-TOAST] Updated: ${data.stageId}`);
        }
        
        updated.set(data.operationId, pipeline);
        return updated;
      });
    });

    // ✅ EVENT: Pipeline complete
    socket.on('pipeline-complete', (data: { 
      operationId: string;
      flowId?: string;
      folderId?: string | null;
      flowName?: string;
    }) => {
      logger.debug(`[PIPELINE-MODAL] Pipeline complete: ${data.operationId}`);
      
      setActivePipelines(prev => {
        const updated = new Map(prev);
        const pipeline = updated.get(data.operationId);
        if (pipeline) {
          // Force all stages to complete (safety net for missed stage-update events)
          pipeline.stages = pipeline.stages.map(s => ({
            ...s,
            status: s.status === 'error' ? 'error' : 'complete' as const
          }));
          if (data.flowId) {
            pipeline.flowId = data.flowId;
            pipeline.folderId = data.folderId;
            pipeline.flowName = data.flowName;
          }
          updated.set(data.operationId, pipeline);
          logger.debug(`[PIPELINE-MODAL] Updated pipeline with flow metadata: ${data.flowId}`);
          
          // If backgrounded, show success toast
          if (isBackgrounded && backgroundToastId) {
            updateToast(backgroundToastId, {
              type: 'success',
              title: '✅ Test Ready!',
              message: `${data.flowName} has been processed successfully`,
              duration: 0, // ✅ MANUAL DISMISS ONLY (was 10000)
              dismissible: true,
              showProgress: false,
              action: {
                label: 'View Test',
                onClick: () => {
                  // Use existing navigation logic
                  navigate('/flows', {
                    state: {
                      navigateToFlow: {
                        flowId: data.flowId!,
                        folderId: data.folderId,
                        flowName: data.flowName!,
                        timestamp: Date.now()
                      }
                    }
                  });
                  logger.debug(`[PIPELINE-TOAST] Navigating to flow: ${data.flowId}`);
                }
              }
            });
            
            logger.debug(`[PIPELINE-TOAST] Success toast shown for ${data.flowName}`);
            
            // ✅ Reset background state (let user manually close toast)
            setIsBackgrounded(false);
            setBackgroundToastId(null);
            // Pipeline remains open until user manually closes it
          }
        }
        return updated;
      });
      
      // Leave room
      socket.emit('leave-pipeline-room', { operationId: data.operationId });
      
      // ✅ NO AUTO-CLOSE - User must manually close the modal
      logger.debug(`[PIPELINE-MODAL] Pipeline complete: ${data.operationId}, waiting for manual close`);
    });

    // ✅ EVENT: Pipeline error
    socket.on('pipeline-error', (data: { operationId: string; error: string }) => {
      logger.error(`[PIPELINE-MODAL] Pipeline error: ${data.operationId}`, { data: data.error });
      
      // Mark current stage as error
      setActivePipelines(prev => {
        const updated = new Map(prev);
        const pipeline = updated.get(data.operationId);
        if (!pipeline) return prev;

        // Find the first processing stage and mark it as error
        const hasProcessingStage = pipeline.stages.some(s => s.status === 'processing');
        
        if (hasProcessingStage) {
          // Normal case: mark the processing stage as error
          pipeline.stages = pipeline.stages.map(stage =>
            stage.status === 'processing'
              ? { ...stage, status: 'error', errorMessage: data.error }
              : stage
          );
        } else {
          // Race condition: error arrived before stage-update was received
          // Mark the first 'pending' stage as error so hasError becomes true
          let markedFirst = false;
          pipeline.stages = pipeline.stages.map(stage => {
            if (!markedFirst && stage.status === 'pending') {
              markedFirst = true;
              return { ...stage, status: 'error', errorMessage: data.error };
            }
            return stage;
          });
          logger.warn(`[PIPELINE-MODAL] No processing stage found for error — marked first pending stage`, {});
        }
        
        // If backgrounded, show error toast
        if (isBackgrounded && backgroundToastId) {
          updateToast(backgroundToastId, {
            type: 'error',
            title: '❌ Processing Failed',
            message: `${pipeline.flowName}: ${data.error}`,
            duration: 0, // Manual dismiss only
            dismissible: true,
            showProgress: false,
            action: {
              label: 'View Details',
              onClick: () => {
                setIsBackgrounded(false); // Re-open modal to show error
                logger.debug('[PIPELINE-TOAST] Re-opening modal to show error');
              }
            }
          });
          
          logger.error(`[PIPELINE-TOAST] Error toast shown for ${pipeline.flowName}`);
        }
        
        updated.set(data.operationId, pipeline);
        return updated;
      });
    });
    
    // ============================================================================
    // INDUSTRY STANDARD: Event Sourcing - Dynamic Pipeline Modification
    // ============================================================================
    // Pattern: React to stage injection events (Kafka, Event Store, AWS EventBridge)
    // Allows pipeline structure to change during execution
    // ============================================================================
    
    // ✅ EVENT: Inject stage dynamically (e.g., file-detection when file upload detected)
    socket.on('pipeline-inject-stage', (data: {
      operationId: string;
      stage: {
        id: string;
        name: string;
        description: string;
        insertBefore?: string;
        required: boolean;
      };
    }) => {
      logger.debug(`[PIPELINE-MODAL] Injecting stage: ${data.stage.id} (insertBefore: ${data.stage.insertBefore || 'end'})`);
      
      setActivePipelines(prev => {
        const updated = new Map(prev);
        const pipeline = updated.get(data.operationId);
        
        if (!pipeline) {
          logger.warn(`[PIPELINE-MODAL] Pipeline not found: ${data.operationId}`);
          return prev;
        }
        
        // Prevent duplicate stages
        if (pipeline.stages.some(s => s.id === data.stage.id)) {
          logger.warn(`[PIPELINE-MODAL] Stage ${data.stage.id} already exists, skipping injection`);
          return prev;
        }
        
        const newStage = {
          ...data.stage,
          status: 'pending' as const
        };
        
        // Insert at correct position
        if (data.stage.insertBefore) {
          const insertIndex = pipeline.stages.findIndex(s => s.id === data.stage.insertBefore);
          if (insertIndex !== -1) {
            pipeline.stages.splice(insertIndex, 0, newStage);
            logger.debug(`[PIPELINE-MODAL] Inserted ${data.stage.id} before ${data.stage.insertBefore} (position ${insertIndex})`);
          } else {
            logger.warn('Insert target not found, appending to end', { component: 'PIPELINE-MODAL', target: data.stage.insertBefore });
            pipeline.stages.push(newStage);
          }
        } else {
          pipeline.stages.push(newStage);
        }
        
        updated.set(data.operationId, { ...pipeline });
        logger.debug(`[PIPELINE-MODAL] Total stages now: ${pipeline.stages.length}`);
        
        return updated;
      });
    });

    // Cleanup: remove listeners when socket changes or component unmounts
    // Socket lifecycle (connect/disconnect) is managed by SocketContext — NOT here
    return () => {
      socket.off('recording-stopped');
      socket.off('pipeline-started');
      socket.off('pipeline-stage-update');
      socket.off('pipeline-complete');
      socket.off('pipeline-error');
      socket.off('pipeline-inject-stage');
    };
  }, [socket, isBackgrounded, backgroundToastId, navigate, showToast, updateToast]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleClose = () => {
    if (!currentPipeline) return;
    
    currentPipeline.onClose?.();
    
    // Remove from active pipelines
    setActivePipelines(prev => {
      const updated = new Map(prev);
      updated.delete(currentPipeline.operationId);
      return updated;
    });

    // Reset background state
    setIsBackgrounded(false);
    setBackgroundToastId(null);

    // Leave WebSocket room
    socket?.emit('leave-pipeline-room', { operationId: currentPipeline.operationId });
  };

  const handleRunInBackground = useCallback(() => {
    if (!currentPipeline) return;
    
    logger.debug(`[PIPELINE-MODAL] Backgrounding pipeline: ${currentPipeline.operationId}`);
    
    // Get current stage info
    const currentStage = currentPipeline.stages.find(s => s.status === 'processing');
    
    logger.debug(`[PIPELINE-MODAL] Backgrounding at stage: ${currentStage?.name}`);
    
    // Show background toast with View Pipeline action (no counter - simpler UI)
    const toastId = showToast({
      type: 'info',
      title: '⚙️ Processing in background',
      message: `${currentPipeline.flowName} - ${currentStage?.name || 'Processing'}`,
      duration: 0, // Manual dismiss only
      position: 'bottom-right',
      dismissible: true, // Allow manual close
      showProgress: false,
      action: {
        label: 'View Pipeline',
        onClick: () => {
          setIsBackgrounded(false); // Re-open modal
          logger.debug('[PIPELINE-MODAL] Re-opening modal from background');
        }
      }
    });
    
    setBackgroundToastId(toastId);
    setIsBackgrounded(true);
    
    logger.debug(`[PIPELINE-MODAL] Backgrounded with toast ID: ${toastId}`);
  }, [currentPipeline, showToast]);

  const handleViewTest = () => {
    if (!currentPipeline || !currentPipeline.flowId) {
      logger.warn('[PIPELINE-MODAL] Cannot navigate: flowId missing');
      return;
    }
    
    const { flowId, folderId, flowName } = currentPipeline;
    
    logger.debug(`[NAVIGATION] Navigating to flow: ${flowId} in folder: ${folderId || 'Root'}`);
    
    // Industry Standard: React Router State for Navigation Data
    // Why: Avoids race conditions, deterministic, type-safe, reliable
    // Pattern: React Router, Next.js, Remix, Vue Router, Angular Router
    navigate('/flows', {
      state: {
        navigateToFlow: {
          flowId,
          folderId,
          flowName,
          timestamp: Date.now()  // Ensure uniqueness for re-navigation
        }
      }
    });
    
    logger.debug(`[NAVIGATION] Navigation initiated with state for ${flowName}`);
    
    // Call optional callback & close modal
    currentPipeline.onComplete?.();
    handleClose();
  };

  // ============================================================================
  // RETRY AI ENHANCEMENT
  // ============================================================================
  // INDUSTRY STANDARD: Idempotent retry with optimistic UI
  // Pattern: Stripe retry invoice, GitHub Actions re-run, AWS Lambda retry
  // ============================================================================
  const handleRetryAI = async () => {
    if (!currentPipeline?.flowId) {
      logger.warn('[PIPELINE-MODAL] Cannot retry: flowId missing');
      return;
    }

    const { flowId, operationId } = currentPipeline;
    
    logger.debug(`[PIPELINE-MODAL] Retrying AI enhancement for flow: ${flowId}`);
    setRetryingStage('ai-enhancement');

    try {
      const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/retry-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ operationId })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Retry failed');
      }

      logger.debug('[PIPELINE-MODAL] AI retry initiated successfully');
      // WebSocket will handle UI updates (stage status changes)
      
    } catch (error) {
      logger.error('[PIPELINE-MODAL] Retry request failed:', { error: getErrorMessage(error) });
      
      showToast({
        type: 'error',
        title: 'Retry Failed',
        message: error instanceof Error ? error.message : 'Could not retry AI enhancement',
        duration: 5000
      });
    } finally {
      setRetryingStage(null);
    }
  };

  // ============================================================================
  // ABORT AI GENERATION
  // ============================================================================
  // Only shown for ai-generation pipelines while still processing
  // Calls POST /api/ai-tests/:operationId/abort
  // ============================================================================
  const handleAbort = async () => {
    if (!currentPipeline) return;
    
    setIsAborting(true);
    logger.debug(`[PIPELINE-MODAL] Aborting AI generation: ${currentPipeline.operationId}`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-tests/${currentPipeline.operationId}/abort`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToast({
          type: 'warning',
          title: '⏹ Generation Aborted',
          message: 'AI test generation was stopped. No test was saved.',
          duration: 5000
        });
        // Close the pipeline modal
        handleClose();
      } else if (response.status === 404) {
        // Operation already completed or errored out — just close the modal
        showToast({
          type: 'info',
          title: 'Operation Already Finished',
          message: 'The generation has already completed or errored. Closing.',
          duration: 4000
        });
        handleClose();
      } else {
        showToast({
          type: 'error',
          title: 'Abort Failed',
          message: result.error || 'Could not abort generation',
          duration: 5000
        });
      }
    } catch (error) {
      logger.error('[PIPELINE-MODAL] Abort request failed:', { error: getErrorMessage(error) });
      showToast({
        type: 'error',
        title: 'Abort Failed',
        message: 'Network error while aborting generation',
        duration: 5000
      });
    } finally {
      setIsAborting(false);
    }
  };

  // ── Derived: is this an AI generation pipeline? ────────────────────────
  const isAIGeneration = currentPipeline?.operationType === 'ai-generation';
  const isStillProcessing = !allComplete && !hasError;

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!currentPipeline) return null;
  
  // Hide modal when backgrounded
  if (isBackgrounded) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
      style={{ pointerEvents: 'auto' }}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
        style={{ animation: 'fadeIn 0.3s ease-in-out' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {allComplete 
                ? `✅ ${currentPipeline.completionMessage}` 
                : hasError
                ? `❌ ${currentPipeline.title} - Error`
                : currentPipeline.title
              }
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Operation: {currentPipeline.operationId.substring(0, 30)}...
            </p>
          </div>
          {/* X button: only visible when pipeline is done (complete or error).
              While processing, user must use "Abort" (AI gen) or "Run in Background" */}
          {(allComplete || hasError) && (
            <button
              onClick={handleClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Close pipeline modal"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Stage List */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {currentPipeline.stages.map((stage) => (
            <div key={stage.id} className="mb-4 flex items-start gap-3">
              {/* Status Icon */}
              <div className="flex-shrink-0 mt-1">
                {stage.status === 'complete' && (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                )}
                {stage.status === 'processing' && (
                  <Loader className="w-6 h-6 text-blue-500 animate-spin" />
                )}
                {stage.status === 'pending' && (
                  <Circle className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                )}
                {stage.status === 'error' && (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
              </div>

              {/* Stage Info */}
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  {stage.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stage.description}
                </p>
                
                {/* Estimated Time (for processing) */}
                {stage.estimatedTime && stage.status === 'processing' && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {stage.estimatedTime}
                  </p>
                )}

                {/* Details (for completed stages) */}
                {stage.details && stage.details.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {stage.details.map((detail, idx) => (
                      <li key={idx} className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-1">
                        <span className="text-green-500 mt-0.5">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Error Message */}
                {stage.errorMessage && stage.status === 'error' && (
                  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                    <p className="text-xs text-red-700 dark:text-red-300">{stage.errorMessage}</p>
                    
                    {/* Retry button - only for AI Enhancement stage */}
                    {stage.id === 'ai-enhancement' && currentPipeline.flowId && (
                      <button
                        onClick={handleRetryAI}
                        disabled={retryingStage === 'ai-enhancement'}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium 
                                   text-white bg-blue-600 rounded-md hover:bg-blue-700 
                                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                   dark:bg-blue-700 dark:hover:bg-blue-600"
                        aria-label="Retry AI Enhancement"
                      >
                        {retryingStage === 'ai-enhancement' ? (
                          <>
                            <Loader className="w-3 h-3 animate-spin" />
                            Retrying...
                          </>
                        ) : (
                          <>
                            <RotateCw className="w-3 h-3" />
                            Retry AI Enhancement
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex justify-between gap-3">
          {/* Left side: Abort button for AI generation while processing */}
          <div>
            {isAIGeneration && isStillProcessing && (
              <button
                onClick={handleAbort}
                disabled={isAborting}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Abort AI test generation"
              >
                {isAborting ? (
                  <>
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                    Aborting...
                  </>
                ) : (
                  <>
                    <StopCircle className="w-3.5 h-3.5" />
                    Abort
                  </>
                )}
              </button>
            )}
          </div>

          {/* Right side: action buttons */}
          <div className="flex gap-3">
            {/* Run in Background button - show when processing */}
            {isStillProcessing && !isBackgrounded && (
              <button
                onClick={handleRunInBackground}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
              >
                Run in Background
              </button>
            )}
            
            {allComplete && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Close
                </button>
                {/* View Test — works for both recording and AI generation */}
                {(currentPipeline.operationType === 'recording' || isAIGeneration) && currentPipeline.flowId && (
                  <button
                    onClick={handleViewTest}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    View Test
                  </button>
                )}
              </>
            )}
            {hasError && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Close
              </button>
            )}
            {isStillProcessing && (
              <button
                disabled
                className="px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-slate-700 rounded-md cursor-not-allowed"
              >
                Processing... Please wait
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

